import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { subscribeCampaigns } from '../services/campaigns';
import type { Campaign, CampaignStatus } from '../../functions/src/shared/types';
import { formatTimestamp, statusColor } from '../lib/format';

export default function CampaignsPage() {
  const [campaigns, setCampaigns] = useState<Campaign[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(
    () =>
      subscribeCampaigns(
        (next) => {
          setCampaigns(next);
          setError(null);
        },
        (err) => setError(err.message),
      ),
    [],
  );

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="h5" component="h1">
          Campaigns
        </Typography>
        <Button variant="contained" component={RouterLink} to="/campaigns/new">
          New campaign
        </Button>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      {campaigns === null && !error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress aria-label="Loading campaigns" />
        </Box>
      )}

      {campaigns?.length === 0 && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" gutterBottom>
            No campaigns yet.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Paste an email, upload a CSV of guests, and send.
          </Typography>
        </Paper>
      )}

      {campaigns && campaigns.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell component="th" scope="col">Name</TableCell>
                <TableCell component="th" scope="col">Status</TableCell>
                <TableCell component="th" scope="col" align="right">Sent</TableCell>
                <TableCell component="th" scope="col" align="right">Remaining</TableCell>
                <TableCell component="th" scope="col" align="right">Problems</TableCell>
                <TableCell component="th" scope="col">Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns.map((campaign) => {
                const stats = campaign.stats;
                const problems = stats.failed + stats.bounced + stats.complained;
                return (
                  <TableRow key={campaign.id} hover>
                    <TableCell>
                      <Link component={RouterLink} to={`/campaigns/${campaign.id}`}>
                        {campaign.name}
                      </Link>
                      <Typography variant="body2" color="text.secondary">
                        {campaign.subject}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={campaign.status}
                        color={statusColor(campaign.status as CampaignStatus)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {stats.sent} / {stats.total}
                    </TableCell>
                    <TableCell align="right">{stats.queued}</TableCell>
                    <TableCell align="right">{problems > 0 ? problems : '—'}</TableCell>
                    <TableCell>{formatTimestamp(campaign.createdAt)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
}
